'use client';

import Link from 'next/link';
import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';
import ActionIconButton from '@/components/ActionIconButton';
import AppFooter from '@/components/AppFooter';
import AppLogo from '@/components/AppLogo';
import BottomNavigation from '@/components/BottomNavigation';
import CategoryBudgetCard from '@/components/CategoryBudgetCard';
import { useAppSettings } from '@/hooks/useAppSettings';
import { useCategories } from '@/hooks/useCategories';
import { useMonthlyStats } from '@/hooks/useMonthlyStats';
import { useRecurringExpenses } from '@/hooks/useRecurringExpenses';
import { useSavingsGoal } from '@/hooks/useSavingsGoal';
import { useTransactions } from '@/hooks/useTransactions';
import { formatMoney } from '@/lib/money';

export default function HomePage() {
  const { stats, loading, error: statsError } = useMonthlyStats();
  const { categories, loading: loadingCategories, error: categoriesError } = useCategories();
  const { transactions, loading: loadingTransactions, error: transactionsError } = useTransactions();
  const {
    recurringExpenses,
    addRecurringExpense,
    updateRecurringExpense,
    deleteRecurringExpense,
    loading: loadingRecurring,
    error: recurringError,
  } = useRecurringExpenses();
  const { goal, saveGoal, loading: loadingGoal, error: goalError } = useSavingsGoal();
  const { settings, error: settingsError } = useAppSettings();
  const [ready, setReady] = useState(false);
  const [recurringName, setRecurringName] = useState('');
  const [recurringAmount, setRecurringAmount] = useState('');
  const [recurringCategoryId, setRecurringCategoryId] = useState('');
  const [recurringDay, setRecurringDay] = useState('1');
  const [editingRecurringId, setEditingRecurringId] = useState<string | null>(null);
  const [editingRecurringName, setEditingRecurringName] = useState('');
  const [editingRecurringAmount, setEditingRecurringAmount] = useState('');
  const [editingRecurringCategoryId, setEditingRecurringCategoryId] = useState('');
  const [editingRecurringDay, setEditingRecurringDay] = useState('1');
  const [goalAmount, setGoalAmount] = useState('');

  useEffect(() => {
    setReady(true);
  }, []);

  useEffect(() => {
    if (!loadingGoal) {
      setGoalAmount(goal?.amount ? String(goal.amount) : '');
    }
  }, [goal, loadingGoal]);

  const money = (value: number) => formatMoney(value, settings.currency);
  const pageError = statsError || categoriesError || transactionsError || recurringError || goalError || settingsError;
  const showOnboarding =
    ready && !loadingCategories && !loadingTransactions && categories.length === 0 && transactions.length === 0;

  const alertCategories = categories.filter((category) => {
    const spent = stats.categorySpending[category.id] ?? 0;
    return category.monthlyBudget !== undefined && category.monthlyBudget > 0 && spent > category.monthlyBudget;
  });

  const activeRecurringExpenses = recurringExpenses.filter((item) => item.active);
  const recurringActiveTotal = activeRecurringExpenses.reduce((sum, item) => sum + item.amount, 0);
  const recurringTotal = recurringExpenses.reduce((sum, item) => sum + item.amount, 0);
  const monthBalanceTone = stats.monthActualBalance >= 0 ? 'text-brand-200' : 'text-rose-200';
  const projectedBalanceTone = stats.monthBalance >= 0 ? 'text-brand-300' : 'text-rose-300';

  const addRecurring = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const amount = Number(recurringAmount);
    const dayOfMonth = Number(recurringDay);
    if (
      !recurringName.trim() ||
      isNaN(amount) ||
      amount <= 0 ||
      !Number.isInteger(dayOfMonth) ||
      dayOfMonth < 1 ||
      dayOfMonth > 31
    ) {
      return;
    }

    await addRecurringExpense({
      name: recurringName.trim(),
      amount,
      categoryId: recurringCategoryId || undefined,
      dayOfMonth,
      active: true,
    });
    setRecurringName('');
    setRecurringAmount('');
    setRecurringCategoryId('');
    setRecurringDay('1');
  };

  const startEditRecurring = (id: string) => {
    const item = recurringExpenses.find((expense) => expense.id === id);
    if (!item) return;
    setEditingRecurringId(id);
    setEditingRecurringName(item.name);
    setEditingRecurringAmount(String(item.amount));
    setEditingRecurringCategoryId(item.categoryId ?? '');
    setEditingRecurringDay(String(item.dayOfMonth ?? 1));
  };

  const cancelEditRecurring = () => {
    setEditingRecurringId(null);
    setEditingRecurringName('');
    setEditingRecurringAmount('');
    setEditingRecurringCategoryId('');
    setEditingRecurringDay('1');
  };

  const saveRecurringEdit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingRecurringId) return;

    const amount = Number(editingRecurringAmount);
    const dayOfMonth = Number(editingRecurringDay);
    const current = recurringExpenses.find((item) => item.id === editingRecurringId);
    if (
      !current ||
      !editingRecurringName.trim() ||
      isNaN(amount) ||
      amount <= 0 ||
      !Number.isInteger(dayOfMonth) ||
      dayOfMonth < 1 ||
      dayOfMonth > 31
    ) {
      return;
    }

    await updateRecurringExpense(editingRecurringId, {
      name: editingRecurringName.trim(),
      amount,
      categoryId: editingRecurringCategoryId || undefined,
      dayOfMonth,
      active: current.active,
    });
    cancelEditRecurring();
  };

  const updateSavingsGoal = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const amount = goalAmount.trim() === '' ? 0 : Number(goalAmount);
    if (isNaN(amount) || amount < 0) return;
    await saveGoal(amount);
  };

  return (
    <main className="page-shell">
      <div className="page-inner">
        {pageError ? (
          <div className="rounded-2xl border border-rose-900/70 bg-rose-950 p-4 text-sm text-rose-200">
            {pageError}
          </div>
        ) : null}

        <section className="overflow-hidden rounded-2xl border border-brand-500/20 bg-slate-900 shadow-sm">
          <div className="bg-brand-700 px-4 py-5 text-white min-[380px]:px-5">
            <div className="flex flex-col items-stretch gap-4 min-[380px]:flex-row min-[380px]:items-start min-[380px]:justify-between">
              <div className="min-w-0">
                <AppLogo className="text-brand-100" markClassName="border-white/25" textClassName="text-brand-100" />
                <h1 className="mt-1 text-xl font-semibold leading-tight text-white min-[380px]:text-2xl">Accueil budget</h1>
              </div>
              <Link
                href="/transactions"
                className="inline-flex min-h-12 w-full shrink-0 items-center justify-center rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-white/30 min-[380px]:w-auto"
              >
                + Ajouter
              </Link>
            </div>

            <div className="mt-7">
              <p className="text-sm font-medium text-brand-100">Solde réel du mois</p>
              <p className={`mt-2 text-3xl font-semibold leading-tight min-[380px]:text-4xl ${monthBalanceTone}`}>
                {loading ? '...' : money(stats.monthActualBalance)}
              </p>
            </div>
          </div>

          <div className="grid gap-px bg-slate-800 sm:grid-cols-3">
            <div className="bg-slate-900 p-4">
                <p className="text-sm font-medium text-slate-400">Entrées</p>
              <p className="mt-2 text-2xl font-semibold text-emerald-300">
                {loading ? '...' : money(stats.monthIncome)}
              </p>
            </div>
            <div className="bg-slate-900 p-4">
                <p className="text-sm font-medium text-slate-400">Dépenses</p>
              <p className="mt-2 text-2xl font-semibold text-rose-300">
                {loading ? '...' : money(stats.monthExpense)}
              </p>
            </div>
            <div className="bg-slate-900 p-4">
              <p className="text-sm font-medium text-slate-400">Reste budgets</p>
              <p className="mt-2 text-2xl font-semibold text-slate-100">
                {loading ? '...' : money(stats.globalBudgetRemaining)}
              </p>
            </div>
          </div>

          <div className="bg-slate-950 px-4 py-4 min-[380px]:px-5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-medium text-slate-300">Après charges fixes et objectif</p>
                <p className="mt-1 text-xs text-slate-500">Projection du mois si tout reste prévu.</p>
              </div>
              <p className={`text-xl font-semibold ${projectedBalanceTone}`}>
                {loading ? '...' : money(stats.monthBalance)}
              </p>
            </div>
          </div>
        </section>

        {showOnboarding ? (
          <section className="rounded-2xl border border-brand-900/70 bg-brand-950/40 p-5">
            <h2 className="text-xl font-semibold text-slate-100">Commencez tranquillement</h2>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              Licaio fonctionne sans compte et sans serveur. Vos données restent sur cet appareil.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              <Link href="/budgets" className="rounded-2xl bg-slate-950 p-4 transition hover:bg-slate-900">
                <span className="text-sm font-semibold text-slate-100">Catégories</span>
                <span className="mt-2 block text-xs leading-5 text-slate-400">
                  Quelques limites suffisent pour démarrer.
                </span>
              </Link>
              <Link href="/transactions" className="rounded-2xl bg-slate-950 p-4 transition hover:bg-slate-900">
                <span className="text-sm font-semibold text-slate-100">Première transaction</span>
                <span className="mt-2 block text-xs leading-5 text-slate-400">
                  Ajoutez une dépense ou une entrée.
                </span>
              </Link>
              <Link href="/settings" className="rounded-2xl bg-slate-950 p-4 transition hover:bg-slate-900">
                <span className="text-sm font-semibold text-slate-100">Sauvegarde</span>
                <span className="mt-2 block text-xs leading-5 text-slate-400">
                  Exportez vos données de temps en temps.
                </span>
              </Link>
            </div>
          </section>
        ) : null}

        <section className="grid gap-3 sm:grid-cols-3">
          <Link href="/transactions" className="rounded-2xl border border-brand-900/70 bg-brand-950/50 p-4 transition hover:bg-brand-950">
            <p className="text-sm font-semibold text-brand-100">Ajouter une opération</p>
            <p className="mt-2 text-xs leading-5 text-brand-200/80">Le chemin le plus court pour tenir le mois à jour.</p>
          </Link>
          <Link href="/calendar" className="rounded-2xl border border-slate-800 bg-slate-900 p-4 transition hover:bg-slate-800">
            <p className="text-sm font-semibold text-slate-100">Voir le mois</p>
            <p className="mt-2 text-xs leading-5 text-slate-400">Repérez les jours lourds et les jours calmes.</p>
          </Link>
          <Link href="/stats" className="rounded-2xl border border-slate-800 bg-slate-900 p-4 transition hover:bg-slate-800">
            <p className="text-sm font-semibold text-slate-100">Comprendre les dépenses</p>
            <p className="mt-2 text-xs leading-5 text-slate-400">Catégories, tags et évolution du mois.</p>
          </Link>
        </section>

        <section className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="section-card">
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-slate-100">Objectif du mois</h2>
              <p className="mt-2 text-sm text-slate-400">Le montant à mettre de côté avant de vous sentir tranquille.</p>
            </div>
            <form onSubmit={updateSavingsGoal} className="space-y-3">
              <label className="block">
                <span className="text-sm font-medium text-slate-200">Épargne prévue</span>
                <input
                  value={goalAmount}
                  onChange={(event) => setGoalAmount(event.target.value)}
                  placeholder="300"
                  type="number"
                  step="0.01"
                  className="mt-2 w-full px-4 py-3"
                />
              </label>
              <button type="submit" className="primary-button w-full">
                Enregistrer
              </button>
            </form>
          </div>

          <div className="section-card">
            <div className="mb-5 flex flex-col gap-3 min-[420px]:flex-row min-[420px]:items-start min-[420px]:justify-between">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-slate-100">Charges fixes</h2>
                <p className="mt-2 text-sm text-slate-400">Loyer, abonnements et achats prévus chaque mois.</p>
              </div>
              <span className="w-fit rounded-full bg-slate-800 px-3 py-1 text-xs font-semibold text-slate-300">
                {loadingRecurring || !ready ? '...' : `${activeRecurringExpenses.length} actifs`}
              </span>
            </div>

            <div className="mb-5 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl bg-brand-950/50 p-4">
                <p className="text-sm font-medium text-brand-200">Pris en compte</p>
                <p className="mt-2 text-2xl font-semibold text-brand-100">
                  {loadingRecurring || !ready ? '...' : money(recurringActiveTotal)}
                </p>
              </div>
              <div className="rounded-2xl bg-slate-950 p-4">
                <p className="text-sm font-medium text-slate-400">Total enregistré</p>
                <p className="mt-2 text-2xl font-semibold text-slate-100">
                  {loadingRecurring || !ready ? '...' : money(recurringTotal)}
                </p>
              </div>
            </div>

            <form onSubmit={addRecurring} className="grid gap-3 sm:grid-cols-[1fr_120px_92px]">
              <label>
                <span className="text-sm font-medium text-slate-200">Nom</span>
                <input
                  value={recurringName}
                  onChange={(event) => setRecurringName(event.target.value)}
                  placeholder="Loyer, Netflix..."
                  className="mt-2 w-full px-4 py-3"
                />
              </label>
              <label>
                <span className="text-sm font-medium text-slate-200">Montant</span>
                <input
                  value={recurringAmount}
                  onChange={(event) => setRecurringAmount(event.target.value)}
                  placeholder="750"
                  type="number"
                  step="0.01"
                  className="mt-2 w-full px-4 py-3"
                />
              </label>
              <label>
                <span className="text-sm font-medium text-slate-200">Jour</span>
                <input
                  value={recurringDay}
                  onChange={(event) => setRecurringDay(event.target.value)}
                  min={1}
                  max={31}
                  placeholder="1"
                  type="number"
                  className="mt-2 w-full px-4 py-3"
                />
              </label>
              <label className="sm:col-span-2">
                <span className="text-sm font-medium text-slate-200">Catégorie</span>
                <select
                  value={recurringCategoryId}
                  onChange={(event) => setRecurringCategoryId(event.target.value)}
                  className="mt-2 w-full px-4 py-3"
                >
                  <option value="">Aucune</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
              <button type="submit" className="primary-button sm:mt-7">
                Ajouter
              </button>
            </form>
          </div>
        </section>

        <section className="section-card">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-slate-100">Achats récurrents</h2>
            <span className="rounded-full bg-slate-800 px-3 py-1 text-xs font-semibold text-slate-300">
              {recurringExpenses.length}
            </span>
          </div>
          <div className="space-y-3">
            {loadingRecurring || !ready ? (
              <div className="card-muted">Chargement...</div>
            ) : recurringExpenses.length === 0 ? (
              <div className="card-muted text-sm">Aucun achat récurrent pour l'instant.</div>
            ) : (
              recurringExpenses.map((item) => {
                const category = categories.find((cat) => cat.id === item.categoryId);
                return (
                  <div key={item.id} className="rounded-2xl border border-slate-800 bg-slate-950 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-100">{item.name}</p>
                        <p className="mt-1 text-sm text-slate-400">
                          {money(item.amount)} par mois{category ? ` - ${category.name}` : ''}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">Exécuté le {item.dayOfMonth ?? 1} du mois</p>
                      </div>
                      <div className="grid w-full grid-cols-[44px_minmax(72px,1fr)_44px] gap-2 sm:w-auto">
                        <ActionIconButton
                          label="Modifier l'achat récurrent"
                          icon="edit"
                          onClick={() => startEditRecurring(item.id)}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            updateRecurringExpense(item.id, {
                              name: item.name,
                              amount: item.amount,
                              categoryId: item.categoryId,
                              dayOfMonth: item.dayOfMonth ?? 1,
                              active: !item.active,
                            })
                          }
                          className="secondary-button px-2 py-2 text-xs sm:px-3 sm:text-sm"
                        >
                          {item.active ? 'Actif' : 'Inactif'}
                        </button>
                        <ActionIconButton
                          label="Supprimer l'achat récurrent"
                          icon="trash"
                          tone="danger"
                          onClick={() => deleteRecurringExpense(item.id)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="section-card">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-slate-100">Alertes de budget</h2>
              <span className="rounded-full bg-slate-800 px-3 py-1 text-xs font-semibold text-slate-300">
                {alertCategories.length === 0 ? 'OK' : `${alertCategories.length} alerte`}
              </span>
            </div>
            <div className="mt-4 space-y-3">
              {alertCategories.length === 0 ? (
                <div className="rounded-2xl bg-emerald-950 p-4 text-sm text-emerald-200">
                  Tout va bien pour les catégories ce mois-ci.
                </div>
              ) : (
                alertCategories.map((category) => (
                  <div key={category.id} className="rounded-2xl bg-rose-950 p-4 text-sm text-rose-200">
                    <p className="font-semibold">{category.name}</p>
                    <p className="mt-1">Le budget prévu est dépassé.</p>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="section-card">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-slate-100">Catégories</h2>
              <Link href="/budgets" className="text-sm font-semibold text-brand-300">
                Gérer
              </Link>
            </div>
            <div className="grid gap-3">
              {loadingCategories || !ready ? (
                <div className="card-muted">Chargement...</div>
              ) : categories.length === 0 ? (
                <div className="card-muted">Ajoutez une catégorie pour commencer doucement.</div>
              ) : (
                categories.map((category) => (
                  <CategoryBudgetCard key={category.id} category={category} spent={stats.categorySpending[category.id] ?? 0} />
                ))
              )}
            </div>
          </div>
        </section>
      </div>

      {editingRecurringId ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/75 px-4 py-6 sm:items-center">
          <div
            className="w-full max-w-xl rounded-2xl border border-slate-700 bg-slate-900 p-4 shadow-xl min-[380px]:p-5"
            role="dialog"
            aria-modal="true"
            aria-label="Modifier l'achat recurrent"
          >
            <div className="mb-5 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="eyebrow">Modification</p>
                <h2 className="mt-1 text-xl font-semibold text-slate-100">Modifier l'achat récurrent</h2>
              </div>
              <button type="button" onClick={cancelEditRecurring} className="secondary-button shrink-0 px-3 py-2">
                Fermer
              </button>
            </div>

            <form onSubmit={saveRecurringEdit} className="space-y-4">
              <label className="block">
                <span className="text-sm font-medium text-slate-200">Nom</span>
                <input
                  value={editingRecurringName}
                  onChange={(event) => setEditingRecurringName(event.target.value)}
                  placeholder="Loyer, Netflix..."
                  className="mt-2 w-full px-4 py-3"
                />
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="text-sm font-medium text-slate-200">Montant</span>
                  <input
                    value={editingRecurringAmount}
                    onChange={(event) => setEditingRecurringAmount(event.target.value)}
                    placeholder="750"
                    type="number"
                    step="0.01"
                    className="mt-2 w-full px-4 py-3"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-slate-200">Jour d'exécution</span>
                  <input
                    value={editingRecurringDay}
                    onChange={(event) => setEditingRecurringDay(event.target.value)}
                    min={1}
                    max={31}
                    placeholder="1"
                    type="number"
                    className="mt-2 w-full px-4 py-3"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-sm font-medium text-slate-200">Catégorie</span>
                <select
                  value={editingRecurringCategoryId}
                  onChange={(event) => setEditingRecurringCategoryId(event.target.value)}
                  className="mt-2 w-full px-4 py-3"
                >
                  <option value="">Aucune</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex flex-col gap-3 sm:flex-row">
                <button type="submit" className="primary-button sm:flex-1">
                  Enregistrer
                </button>
                <button type="button" onClick={cancelEditRecurring} className="secondary-button sm:flex-1">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <BottomNavigation active="/" />
      <div className="page-inner mt-5">
        <AppFooter />
      </div>
    </main>
  );
}
